import {
  useEmailAliasForEmailForwarding,
  useIndirectTaxCorporationData,
  useIndirectTaxCredential,
} from '@commenda/api';
import {
  IIndirectTaxRegistrationV2Response,
  useAllIndirectTaxRegistrations,
} from '@commenda/api/indirect-tax/registrations';
import { IndirectTaxCredentialBlockType } from '@commenda/api/new-sales-tax/credentials/types';
import { Button, Icons, LoadingIndicator } from '@commenda/kakashi';
import { toTitleCaseSentence } from '@commenda/utils';
import { useEffect, useState } from 'react';

import { DetailRow } from '../../common/components';
import { IndirectTaxRegionHelper } from '../../common/helpers/IndirectTaxRegionHelper';
import { IIndirectTaxFilingDetail } from '../types';

interface IndirectTaxFilingDetailInformationProps {
  filing: IIndirectTaxFilingDetail;
  corporationId: number;
  redirectToRegistrationPage?: (registrationId: string) => void;
}

const IndirectTaxFilingDetailInformation = ({
  filing,
  corporationId,
  redirectToRegistrationPage,
}: IndirectTaxFilingDetailInformationProps) => {
  const [emailList, setEmailList] = useState<string[]>([]);
  const [emailAlias, setEmailAlias] = useState('');

  const { data: corporationData } =
    useIndirectTaxCorporationData(+corporationId);
  const legacyEmailAlias = corporationData?.data?.legacy_email_alias ?? '';

  const { data: emailAliasData } = useEmailAliasForEmailForwarding({
    corporationId: +corporationId,
  });

  // Fetch every registration for the corporation (V2 API, walks all pages) and
  // match on registrationTaxTypeId — the direct 1:1 link to this filing's
  // registration. Fall back to jurisdictionId. Both are region-agnostic
  // (US/Canada/VAT), unlike the legacy US-only state-code matching.
  const { data: registrations } = useAllIndirectTaxRegistrations({
    filters: { corporationId: +corporationId },
  });

  const registrationData: IIndirectTaxRegistrationV2Response | null =
    registrations?.find(
      (reg) => reg.registrationTaxTypeId === filing.registrationTaxTypeId
    ) ??
    registrations?.find(
      (reg) => reg.jurisdictionId === filing.jurisdictionId
    ) ??
    null;

  // Fetch credential details when the matched registration has a credentialId.
  const { data: credentialData, isLoading: isCredentialLoading } =
    useIndirectTaxCredential(
      registrationData?.credentialId || null,
      registrationData?.credentialId ? { corporationId } : null
    );

  const goToRegistrationDetails = () => {
    if (!registrationData?.id || !redirectToRegistrationPage) return;
    redirectToRegistrationPage(registrationData.id);
  };

  useEffect(() => {
    if (emailAliasData?.data?.emails) {
      setEmailList(emailAliasData.data.emails);
    }
  }, [emailAliasData]);

  useEffect(() => {
    if (corporationData?.data?.email_alias) {
      setEmailAlias(corporationData.data.email_alias);
    }
  }, [corporationData]);

  const canViewRegistration = Boolean(
    registrationData && redirectToRegistrationPage
  );

  return (
    <div className="flex flex-col gap-4xl p-3xl">
      {registrationData && (
        <div className="flex flex-col gap-3xl">
          <div className="flex items-center justify-between border-b pb-xl">
            <div className="txt-large-bold text-text-primary">
              Registration details
            </div>
            {canViewRegistration && (
              <Button
                variant="secondary"
                size="sm"
                onClick={goToRegistrationDetails}
                className="gap-sm text-text-secondary"
              >
                <Icons.Eye className="size-4" />
                View Registration
              </Button>
            )}
          </div>
          <div className="flex flex-col">
            <DetailRow
              label="Tax ID"
              value={registrationData.taxRegistrationNumber || '—'}
              copyable
            />
          </div>
        </div>
      )}

      {/* Portal & Credentials */}
      <div className="flex flex-col gap-3xl">
        <div className="flex items-center justify-between border-b pb-xl">
          <div className="txt-large-bold text-text-primary">
            Portal &amp; Credentials
          </div>
        </div>

        {isCredentialLoading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingIndicator />
          </div>
        ) : credentialData ? (
          <div className="flex flex-col">
            {credentialData.name && (
              <DetailRow
                label="Portal"
                value={toTitleCaseSentence(credentialData.name)}
                copyable
              />
            )}

            {credentialData.portalUrl && (
              <DetailRow
                label="Portal URL"
                value={credentialData.portalUrl}
                type="link"
                copyable
              />
            )}

            {credentialData.blocks
              .sort((a, b) => a.blockKey.localeCompare(b.blockKey))
              .map((block) => (
                <DetailRow
                  key={block.id}
                  label={block.blockLabel}
                  value={block.value || '—'}
                  type={
                    block.blockType ===
                    IndirectTaxCredentialBlockType.SecureString
                      ? 'password'
                      : 'text'
                  }
                  copyable
                />
              ))}
          </div>
        ) : (
          <div className="rounded-xl border border-border-primary p-6 flex flex-col items-center gap-3">
            <div className="rounded-full bg-bg-secondary p-3">
              <Icons.Lock01 className="size-5 text-text-tertiary" />
            </div>
            <p className="txt-medium-bold text-text-secondary">
              No credentials added yet
            </p>
            <p className="txt-small-regular text-text-tertiary text-center max-w-sm">
              {IndirectTaxRegionHelper.isCalendarTrackingOnly(filing.region)
                ? 'Portal credentials can be added in registration settings.'
                : 'Add your portal credentials in the registration settings to enable automatic filing.'}
            </p>
            {canViewRegistration && (
              <Button
                variant="secondary"
                size="sm"
                onClick={goToRegistrationDetails}
              >
                <Icons.Eye className="size-3.5" />
                View Registration
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3xl">
        <div className="flex items-center justify-between border-b pb-xl">
          <div className="txt-large-bold text-text-primary">
            Email forwarding preferences
          </div>
        </div>
        <div className="flex flex-col">
          <DetailRow
            label="Commenda Assigned Email"
            value={emailAlias || '—'}
            copyable
          />
          {legacyEmailAlias && (
            <DetailRow label="Legacy email" value={legacyEmailAlias} copyable />
          )}
          {emailList.map((email, index) => (
            <DetailRow
              key={email}
              label={`Email for OTP forwarding ${index + 1}`}
              value={email}
              copyable
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export { IndirectTaxFilingDetailInformation };
